// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { formatDate } from '@shared/ui-kit/utils/format-date';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import { useStepsData } from '@shared/ui-kit';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  resultAndSignatureSchema,
  type ResultAndSignatureFormData,
} from '@shared/advisory/account-workflow/types';
import { mapInstrumentsToProductFamilies } from '@shared/advisory/account-workflow/utils';

export const useTestResultsAndSignature = () => {
  const {
    markStepAsCompleted,
    getFormData,
    saveFormData,
    proposal,
    getAllFormData,
    signAdvisoryContract,
    isSigningAdvisoryContract,
    suitabilityTestResults,
    isAwaitingContractSignature,
    setNavigationFloor,
  } = useAccountWorkflowData();
  const { next, navigate } = useStepsData();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const profilingProposal = proposal!;

  const savedData = getFormData('resultAndSignature');
  const allFormData = getAllFormData();

  const convenienceScore =
    profilingProposal.contract?.appropriateness_score ??
    suitabilityTestResults?.appropriateness?.score ??
    0;

  const riskProfile =
    profilingProposal.contract?.risk_profile ??
    suitabilityTestResults?.suitability?.profile ??
    '';

  const hasPassedConvenienceTest = useMemo(() => {
    return convenienceScore >= 49;
  }, [convenienceScore]);

  const convenientProductFamilies = useMemo(() => {
    return mapInstrumentsToProductFamilies(
      suitabilityTestResults?.appropriateness?.instruments ?? null,
      t,
    );
  }, [suitabilityTestResults?.appropriateness?.instruments, t]);

  const profileExpirationDate = useMemo(() => {
    if (!suitabilityTestResults?.expiration_date) return null;

    return (
      formatDate(
        suitabilityTestResults.expiration_date,
        'standard',
        i18n.language,
      ) ?? null
    );
  }, [suitabilityTestResults?.expiration_date, i18n.language]);

  const form = useForm<ResultAndSignatureFormData>({
    resolver: zodResolver(resultAndSignatureSchema),
    mode: 'onChange',
    defaultValues: savedData || {
      testInformationDeclaration: false,
      precontractualInformationDeclaration: false,
    },
  });

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const handleSign = useCallback(
    async (data: ResultAndSignatureFormData) => {
      saveFormData('resultAndSignature', data);

      try {
        await signAdvisoryContract();

        markStepAsCompleted('result-and-signature');
        setNavigationFloor('confirmation');
        next();
      } catch {
        enqueueSnackbar(
          t('onboarding-process-overview.result-and-signature.sign-error', {
            ns: 'components',
          }),
          {
            variant: 'error',
          },
        );
      }
    },
    [
      enqueueSnackbar,
      markStepAsCompleted,
      next,
      saveFormData,
      setNavigationFloor,
      signAdvisoryContract,
      t,
    ],
  );

  const handleRetakeTest = useCallback(() => {
    if (isAwaitingContractSignature) {
      return;
    }

    setNavigationFloor('contract-review');
    navigate('contract-review');
  }, [navigate, isAwaitingContractSignature, setNavigationFloor]);

  const advisoryType = profilingProposal.advisory_type;

  const advisoryTypeKey = useMemo((): 'discretionary' | 'non-independent' => {
    return advisoryType === 'discretionary'
      ? ('discretionary' as const)
      : ('non-independent' as const);
  }, [advisoryType]);

  const hasRetrocessions = useMemo(() => {
    return advisoryType === 'non-independent';
  }, [advisoryType]);

  const commissionManagedAssets = useMemo(() => {
    return profilingProposal.commission_managed_assets ?? 0;
  }, [profilingProposal.commission_managed_assets]);

  return {
    form,
    handleSign,
    handleRetakeTest,
    canRetakeTest: !isAwaitingContractSignature,
    isAwaitingContractSignature,
    isLoading: isSigningAdvisoryContract,
    isFormValid,
    convenienceScore,
    riskProfile,
    convenientProductFamilies,
    proposal: profilingProposal,
    profileExpirationDate,
    advisoryType,
    advisoryTypeKey,
    hasRetrocessions,
    commissionManagedAssets,
    allFormData,
    hasPassedConvenienceTest,
  };
};
